import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Modal from 'antd/lib/modal';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import Typography from 'antd/lib/typography';
import ExclamationCircleOutlined from '@ant-design/icons/lib/icons/ExclamationCircleOutlined';
import ReloadOutlined from '@ant-design/icons/lib/icons/ReloadOutlined';
import SyncOutlined from '@ant-design/icons/lib/icons/SyncOutlined';
import { QueryKey } from 'src/query-client';
import { MINUTE_MS } from 'src/domain/commonConst';
import { getComputerFormattedTime } from 'src/domain/date/date';
import { useCustomerUtilityBillFileQuery } from 'src/domain/customer-utility-bill-file/queries';
import {
    ICustomerUtilityBillFile,
    billSourceLabel,
    formatCustomerName,
    isFileReparsable,
    isFileReparseInProgress,
} from 'src/domain/customer-utility-bill-file/interface';
import { BILL_BORDER, BILL_SURFACE_MUTED, EMPTY_VALUE } from '../../billStyles';
import { BillStatusTag } from '../../BillStatusTag';
import { BillFilePdfPanel } from './BillFilePdfPanel';
import { BillFileInfoSection } from './BillFileInfoSection';
import { BillFileBillsSection } from './BillFileBillsSection';
import { EditBillModal } from '../../edit-bill/EditBillModal';
import { useBillActions } from '../../table/useBillActions';
import { ICustomerUtilityBill } from 'src/domain/customer-utility-bill/interface';

const { Text } = Typography;

const filePanelStyle = { borderTop: `1px solid ${BILL_BORDER}`, background: BILL_SURFACE_MUTED } as const;

interface Props {
    row: ICustomerUtilityBillFile;
    onClose: () => void;
    // Resolves with whether the file was actually queued for re-parsing (false on a skip or failure).
    onReparse: (row: ICustomerUtilityBillFile) => Promise<boolean>;
}

// Read-only view of a bill file (nothing on a file is editable): status, source PDF, the bills parsed from it,
// and a re-parse action with live tracking. Layout mirrors EditBillModal.
export const EditBillFileModal = ({ row, onClose, onReparse }: Props) => {
    const queryClient = useQueryClient();

    // Once a re-parse is confirmed, poll the file by id every minute until it leaves the in-progress statuses,
    // so the modal reflects the outcome (status + re-created bills) without closing and reopening it.
    const [tracking, setTracking] = useState(false);
    // Locks the buttons for the gap between confirming re-parse and the request settling.
    const [requestingReparse, setRequestingReparse] = useState(false);
    // The bill opened from the bills section (nested EditBillModal).
    const [editingBill, setEditingBill] = useState<ICustomerUtilityBill | null>(null);
    const { save, reparse: reparseBill, isSaving } = useBillActions();

    // Always load the file detail (seeded with the list row so the modal renders instantly) — it is refreshed after a
    // bill is edited, and polled while a re-parse is being tracked.
    const { data: liveFile = row, refetch: refetchFile } = useCustomerUtilityBillFileQuery(row.id, {
        placeholderData: row,
        refetchInterval: (file?: ICustomerUtilityBillFile) => (tracking && file && isFileReparseInProgress(file) ? MINUTE_MS : false),
    });
    const effectiveRow = liveFile;
    const reparseDone = tracking && !isFileReparseInProgress(liveFile);
    const reparseRunning = requestingReparse || (tracking && !reparseDone);
    const reparsable = isFileReparsable(effectiveRow);

    // Once the re-parse settles, refresh the files list and the bills list so both reflect the new outcome.
    useEffect(() => {
        if (!reparseDone) return;
        queryClient.invalidateQueries([QueryKey.CUSTOMER_BILL_FILES]);
        queryClient.invalidateQueries([QueryKey.CUSTOMER_BILL_REPORTS]);
    }, [reparseDone, queryClient]);

    const reparse = () => {
        Modal.confirm({
            title: 'Re-parse this file?',
            icon: <ExclamationCircleOutlined />,
            content:
                'Re-parsing deletes all existing bills created from this file and creates new ones from the new parse. Manual edits and verification on those bills will be lost.',
            okText: 'Re-parse',
            okButtonProps: { danger: true },
            onOk: () => {
                // Reset first so a second re-parse in the same session is a real false -> true flip for the poll.
                setTracking(false);
                setRequestingReparse(true);
                // Start tracking only once the backend confirms the file was queued and we've re-read it (now
                // REPARSE_QUEUED), so the cached pre-reparse (already-terminal) status can't read as "already done".
                onReparse(row)
                    .then(async queued => {
                        if (!queued) return;
                        await refetchFile();
                        setTracking(true);
                    })
                    .finally(() => setRequestingReparse(false));
            },
        });
    };

    // A bill edit / bill re-parse changes this file's bills — refresh the file detail and the files list on close.
    const closeBillModal = () => {
        setEditingBill(null);
        queryClient.invalidateQueries([QueryKey.CUSTOMER_BILL_FILE_DETAIL, row.id]);
        queryClient.invalidateQueries([QueryKey.CUSTOMER_BILL_FILES]);
    };

    const handleSaveBill = (updated: ICustomerUtilityBill, verified: boolean) => {
        save(updated, verified, closeBillModal);
    };

    const customerName = row.source?.customerName ? formatCustomerName(row.source.customerName) : 'Customer utility bill file';
    const subtitle = [row.source?.source ? billSourceLabel(row.source.source) : null, row.id].filter(Boolean).join(' · ');

    return (
        <Modal
            open
            onCancel={onClose}
            closable={!requestingReparse}
            maskClosable={!requestingReparse}
            keyboard={!requestingReparse}
            width="min(1400px, 95vw)"
            style={{ top: 20 }}
            styles={{ content: { overflow: 'hidden' }, body: { padding: 0, height: '80vh', display: 'flex' } }}
            footer={null}
            title={
                <Space size="middle" wrap>
                    <Text strong style={{ fontSize: 16 }}>
                        {customerName}
                    </Text>
                    <BillStatusTag status={effectiveRow.processingStatus} />
                    <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                        {subtitle}
                    </Text>
                </Space>
            }
        >
            <>
                <BillFilePdfPanel fileId={row.id} />

                <div style={{ width: '42%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
                        {tracking && (
                            <Alert
                                style={{ marginBottom: 14 }}
                                type={reparseDone ? 'success' : 'info'}
                                showIcon
                                icon={reparseDone ? undefined : <SyncOutlined spin />}
                                message={reparseDone ? 'Re-parsing complete.' : 'Re-parsing this file — refreshing every minute.'}
                            />
                        )}
                        <BillFileInfoSection file={effectiveRow} />
                        <BillFileBillsSection bills={effectiveRow.bills ?? []} onEditBill={setEditingBill} />
                    </div>

                    <div style={{ ...filePanelStyle, padding: '12px 20px', display: 'flex', justifyContent: 'space-between' }}>
                        <Tooltip
                            title={
                                reparseRunning
                                    ? 'Already re-parsing this file'
                                    : reparsable
                                      ? ''
                                      : 'Re-parse is available once the file is downloaded and the current parsing run has finished'
                            }
                        >
                            <span>
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={reparse}
                                    loading={requestingReparse}
                                    disabled={!reparsable || reparseRunning}
                                >
                                    Re-parse file
                                </Button>
                            </span>
                        </Tooltip>
                        <Button onClick={onClose} disabled={requestingReparse}>
                            Close
                        </Button>
                    </div>

                    <div style={{ ...filePanelStyle, padding: '8px 20px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            Parsed: {effectiveRow.parsedAt ? getComputerFormattedTime(effectiveRow.parsedAt) : EMPTY_VALUE}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            Last updated: {getComputerFormattedTime(effectiveRow.updatedAt)}
                        </Text>
                    </div>
                </div>

                {editingBill && (
                    <EditBillModal row={editingBill} saving={isSaving} onClose={closeBillModal} onSave={handleSaveBill} onReparse={reparseBill} />
                )}
            </>
        </Modal>
    );
};
